// A flat composed the way the sold ones are drawn: one room per bay along
// the facade, a service band behind against the corridor.
//
// The reference is docs/REFERENCE-PLANS.md — 615 flats from 29 Romanian
// developers. What they share, and what this module builds:
//
// - Bays on the window facade, one room each: bedrooms 2.9–3.6 m wide
//   (master 3.3–3.6), a closed kitchen 1.9–2.5 m, the living room 3.5–5.2 m
//   and the rest. Order: bedrooms | kitchen | living, or its mirror, so the
//   kitchen sits between day and night.
// - Depth barely moves with size (7–8 m); a bigger flat takes more bays.
// - Behind the private bays, against the corridor: the entrance hall at the
//   living room's side, the bath beside it with no window, opening off the
//   hall; a strip of hall along the bedrooms only when there is more than
//   one door to reach; from three rooms, a second bath entered from the
//   master bedroom; what is left is a store.
// - The living room runs the full depth, its back part the kitchen when the
//   kitchen is open (which 60–64% of them are).
//
// Coordinates are the flat's own: s along the facade from 0 to F, t from
// the facade (0) to the corridor (D). Every room is a union of rectangles,
// so every wall is straight and every corner square.
import type { RoomKind } from "./layout.ts";

export type Box = [number, number, number, number]; // s0, s1, t0, t1
export interface BayRoom { kind: RoomKind; boxes: Box[] }
export interface BayFlat {
  rooms: BayRoom[];
  /** The front door, on the corridor wall (t = D). */
  door: [number, number];
  /** Where the balcony goes, along s, in front of the living room. */
  balcony: [number, number];
  /** Rooms with a window, by index. */
  windows: number[];
  /** The living room's bay along s, for the sun test. */
  living: [number, number];
}

export interface BayOptions {
  /** Open kitchen at the back of the living room, or a closed kitchen bay with its own window. */
  kitchen?: "open" | "closed";
  /** Living room at s = 0 instead of s = F: neighbours mirror so their baths share a shaft. */
  mirror?: boolean;
}

/** Widths and depths, in metres, from the reference plans. */
export const BAY = {
  hall: 1.25,
  bathWidth: 2.0,
  bathDepth: 2.2,
  master: 3.4,
  bedroom: 3.0,
  minBedroom: 2.8,
  kitchen: 2.2,
  minLiving: 3.6,
  /** A bedroom of about this area sets the depth of the bedroom bays. */
  bedroomArea: 13.5,
  minBedroomDepth: 3.6,
};

const bedroomsOf = (type: number) => Math.max(0, type - 1);

/** The narrowest frontage a type can be drawn in, and the one it wants. */
export function frontage(type: number, kitchen: "open" | "closed" = "open"): { min: number; target: number; max: number } {
  const b = bedroomsOf(type);
  const k = kitchen === "closed" && type > 1 ? BAY.kitchen : 0;
  // a studio needs the bath and the entrance behind its living room
  const min = type === 1 ? Math.max(BAY.minLiving + 1.0, BAY.bathWidth + BAY.hall + 1.2) : b * BAY.minBedroom + k + BAY.minLiving;
  const target = type === 1 ? 6.6 : (b ? (b === 1 ? 3.2 : BAY.master) + (b - 1) * BAY.bedroom : 0) + k + 4.2;
  // past this the flat is a wider one drawn too large: the reference p90 is about target + 1.5 m
  return { min, target, max: type === 1 ? 7.4 : target + 1.6 };
}

/**
 * Compose one single-aspect flat of `type` rooms in a bay of frontage F and
 * depth D. Returns undefined if the type does not fit: the caller picks a
 * smaller one rather than drawing an impossible room.
 */
export function composeSingle(type: number, F: number, D: number, opt: BayOptions = {}): BayFlat | undefined {
  let kitchen = type > 1 ? opt.kitchen ?? "open" : "open";
  const beds = bedroomsOf(type);
  if (F < frontage(type, kitchen).min - 1e-6 || D < 5.2) return undefined;
  // A frontage wider than the type wants does not make a 6 m living room:
  // the bedrooms widen first (master to 3.6, others to 3.3), then the kitchen
  // closes into its own bay, as the reference plans do.
  if (kitchen === "open" && beds && F - (3.6 + (beds - 1) * 3.3) >= BAY.minLiving + 1.8) kitchen = "closed";
  let kw = kitchen === "closed" ? Math.max(1.8, Math.min(BAY.kitchen, F - (beds ? 3.2 + (beds - 1) * BAY.minBedroom : 0) - BAY.minLiving)) : 0;
  let bw = beds ? [beds === 1 ? 3.2 : BAY.master, ...Array(beds - 1).fill(BAY.bedroom)] : [];
  let living = F - bw.reduce((t, w) => t + w, 0) - kw;
  if (living < BAY.minLiving) {
    const need = BAY.minLiving - living;
    const give = bw.reduce((t, w) => t + (w - BAY.minBedroom), 0);
    if (give < need - 1e-6) return undefined;
    bw = bw.map((w) => w - ((w - BAY.minBedroom) / give) * need);
    living = BAY.minLiving;
  }
  // the extra width: bedrooms to 3.6 / 3.3 first, then the closed kitchen to 2.6, then the living room
  if (living > 4.6 && beds) {
    const room = bw.map((w, i) => Math.max(0, (i === 0 ? 3.6 : 3.3) - w));
    const extra = Math.min(living - 4.6, room.reduce((t, x) => t + x, 0));
    const tot = room.reduce((t, x) => t + x, 0) || 1;
    bw = bw.map((w, i) => w + (room[i] / tot) * extra);
    living -= extra;
  }
  if (living > 4.8 && kw) { const k = Math.min(living - 4.8, 2.6 - kw); kw += k; living -= k; }
  // still wider than a living room should be: the bedrooms take it, up to 4 m
  if (living > 5.4 && beds) {
    const room = bw.map((w) => Math.max(0, 4.0 - w)), tot = room.reduce((t, x) => t + x, 0) || 1;
    const extra = Math.min(living - 5.4, tot);
    bw = bw.map((w, i) => w + (room[i] / tot) * extra);
    living -= extra;
  }

  // service band behind the private bays: a strip of hall is needed when more than one door opens off it
  const privates = beds + (kitchen === "closed" ? 1 : 0);
  const strip = privates > 1;
  // bedrooms take the depth the service band leaves, within a bedroom's proportions
  const service = BAY.bathDepth + (strip ? BAY.hall : 0) + 0.05;
  const Db = Math.min(5.0, Math.max(BAY.minBedroomDepth, D - service));
  if (D - Db < BAY.bathDepth - 0.3) return undefined;

  // lay out along s from 0, living at the far end (mirrored at the end)
  const rooms: BayRoom[] = [];
  let s = 0;
  const bedBoxes: Box[] = bw.map((w) => { const b: Box = [s, s + w, 0, Db]; s += w; return b; });
  const kitBox: Box | undefined = kitchen === "closed" ? [s, s + kw, 0, Db] : undefined;
  if (kitBox) s += kw;
  const sP = s; // end of the private bays, start of the living room
  // a living room stops at 7.8 m deep (Vivalia's runs to 8.3); deeper, its back is a store off the hall
  // An open living room with its kitchen stays near 36 m² wall to wall (the reference p90 is 34 net),
  // and never deeper than 7.8 m; what is behind it is a store off the hall.
  const lw0 = F - sP;
  // (beside a closed kitchen it is a smaller room: about 26 m² wall to wall, 19–23 net in the reference)
  let Dl = Math.min(D, 7.8, Math.max(Db + (kitchen === "open" ? 1.6 : 0.6), (kitchen === "open" ? 36 : 26) / lw0));
  if (D - Dl < 0.9) Dl = D;
  const livBox: Box = [sP, F, 0, Dl];

  if (type === 1) {
    // studio: the room on the facade about 4.6 m deep (deeper only in a shallow band), and behind
    // it the bath and the entrance hall in one corner and the kitchen niche, open to the room, beside
    // them; a deep band leaves a store behind the bath and behind the niche
    const sb = BAY.bathWidth + BAY.hall;
    const Dt = Math.max(4.2, Math.min(D - 2.3, Math.max(4.6, 30 / F)));
    let Dl = Math.min(D, 7.8, Dt + Math.max(1.6, (36 - F * Dt) / Math.max(0.5, F - sb)));
    if (D - Dl < 0.9) Dl = D;
    rooms.push({ kind: "living", boxes: [[0, F, 0, Dt], [sb, F, Dt, Dl]] });
    if (Dl < D) rooms.push({ kind: "storage", boxes: [[sb, F, Dl, D]] });
    const tBath = D - Dt - BAY.bathDepth >= 1.0 ? Dt + BAY.bathDepth + 0.2 : D;
    rooms.push({ kind: "bath", boxes: [[0, BAY.bathWidth, Dt, tBath]] });
    if (tBath < D) rooms.push({ kind: "storage", boxes: [[0, BAY.bathWidth, tBath, D]] });
    rooms.push({ kind: "hall", boxes: [[BAY.bathWidth, sb, Dt, D]] });
    const out = finish(rooms, [BAY.bathWidth + BAY.hall / 2, D], F, opt.mirror);
    return { ...out, balcony: mirrorRange([0.3, F - 0.3], F, opt.mirror), living: mirrorRange([0, F], F, opt.mirror) };
  }

  rooms.push({ kind: "living", boxes: [livBox] });
  if (Dl < D) rooms.push({ kind: "storage", boxes: [[sP, F, Dl, D]] });
  bedBoxes.forEach((b) => rooms.push({ kind: "bedroom", boxes: [b] }));
  if (kitBox) rooms.push({ kind: "kitchen", boxes: [kitBox] });

  // the entrance column against the living room, the full service depth
  const right = sP - BAY.hall;
  const hall: Box[] = [[right, sP, Db, D]];
  // the strip along the private bays reaches 1.1 m into the farthest one, for its door
  const reach = strip ? Math.max(0, bedBoxes[0][1] - 1.1) : right;
  if (strip) hall.push([reach, right, Db, Db + BAY.hall]);
  rooms.push({ kind: "hall", boxes: hall });

  // behind the strip, or straight behind the private bays: the bath beside the entrance, the rest a store
  const tb = strip ? Db + BAY.hall : Db;
  const from = strip ? reach : 0;
  const bathW = Math.min(BAY.bathWidth + 0.4, right - from);
  if (bathW < 1.6 || bathW * (D - tb) < 3.4) return undefined;
  rooms.push({ kind: "bath", boxes: [[right - bathW, right, tb, D]] });
  // the rest of the band is stores off the hall, none much over 5 m²
  let x = right - bathW;
  while (x - from > 0.6) {
    // the last store takes whatever is left, so no sliver stays unassigned
    const w = x - from < 2 * Math.max(1.2, 5 / (D - tb)) ? x - from : Math.max(1.2, 5 / (D - tb));
    rooms.push({ kind: "storage", boxes: [[x - w, x, tb, D]] });
    x -= w;
  }
  // a sliver under 0.6 m goes to the room beside it, the bath or the last store
  if (x - from > 0.01) rooms[rooms.length - 1].boxes[0][0] = from;
  // behind the master bedroom, beyond the strip: from three rooms an en-suite in the back corner
  // with a dressing in front of it, entered from the bedroom; else the bedroom's dressing
  if (strip && reach > 0.6) {
    const ensuite = type >= 3 && reach >= 1.6 && reach * Math.min(2.4, D - Db) >= 3.4;
    if (ensuite && D - Db - 2.4 >= 0.6) {
      // the space in front of the en-suite is the master bedroom's dressing, part of the room,
      // so the bath is entered through the bedroom and not off the hall
      rooms[1].boxes.push([0, reach, Db, D - 2.4]);
      rooms.push({ kind: "bath", boxes: [[0, reach, D - 2.4, D]] });
    } else rooms.push({ kind: ensuite ? "bath" : "storage", boxes: [[0, reach, Db, D]] });
  }

  const out = finish(rooms, [sP - BAY.hall / 2, D], F, opt.mirror);
  const lw = F - sP;
  return { ...out, balcony: mirrorRange([sP + 0.3, F - 0.3 - Math.max(0, lw - 4.6)], F, opt.mirror), living: mirrorRange([sP, F], F, opt.mirror) };
}

function mirrorRange([a, b]: [number, number], F: number, mirror?: boolean): [number, number] {
  return mirror ? [F - b, F - a] : [a, b];
}

/** Mirror if asked, and list the rooms with a facade wall (t = 0) as the ones with a window. */
function finish(rooms: BayRoom[], door: [number, number], F: number, mirror?: boolean): { rooms: BayRoom[]; door: [number, number]; windows: number[] } {
  const m = (b: Box): Box => (mirror ? [F - b[1], F - b[0], b[2], b[3]] : b);
  const out = rooms.map((r) => ({ kind: r.kind, boxes: r.boxes.map(m).filter((b) => b[1] - b[0] > 0.05 && b[3] - b[2] > 0.05) })).filter((r) => r.boxes.length);
  const windows = out.map((r, i) => (r.kind !== "bath" && r.kind !== "hall" && r.kind !== "storage" && r.boxes.some((b) => b[2] < 1e-6) ? i : -1)).filter((i) => i >= 0);
  return { rooms: out, door: mirror ? [F - door[0], door[1]] : door, windows };
}

/** Area of a bay room. */
export const boxArea = (r: BayRoom) => r.boxes.reduce((t, b) => t + (b[1] - b[0]) * (b[3] - b[2]), 0);

/**
 * A through flat, entered from a core landing at one end (s = 0 when
 * `doorAtStart`, else s = F) at mid-depth: the decomandat flat of the slim
 * Romanian block. Facade A (t = 0) takes the living room, and the closed
 * kitchen when there is one; facade B (t = D) the bedrooms; between them a
 * service band whose hall runs from the landing door as far as the rooms
 * that open off it, with the bath at its far end. A side that comes out much
 * wider than the other takes a bedroom across, so both facades end level.
 */
export function composeThrough(type: number, F: number, D: number, opt: BayOptions & { doorAtStart?: boolean } = {}): BayFlat | undefined {
  if (type < 2 || D < 9) return undefined;
  const beds = bedroomsOf(type);
  const kitchen = opt.kitchen ?? "open";
  const band = BAY.bathDepth + 0.2; // the service band, mid-depth
  const Da = (D - band) / 2, tb0 = Da, tb1 = Da + band;
  // The two facades' bays. A: the living room, its kitchen open in it (or,
  // asked for closed, a kitchen bay beside it at the door end — next to the
  // living room, never across the hall among the bedrooms). B: the
  // bedrooms. A bedroom crosses to the living room's facade only when the
  // sides differ by more than 3 m, and then by the door, so the hall stays
  // short. Width the bedrooms cannot use (past 3.6 / 3.3 m) is a wet bay by
  // the door: the bath against the band, the master bedroom's dressing in
  // front of it on the facade. The band is then only hall, and what the hall
  // does not need opens into the living room.
  const sum = (xs: { w: number }[]) => xs.reduce((t, x) => t + x.w, 0);
  const A: { kind: RoomKind; w: number }[] = [{ kind: "living", w: BAY.minLiving }];
  const B: { kind: RoomKind; w: number }[] = Array.from({ length: beds }, (_, i) => ({ kind: "bedroom" as RoomKind, w: i === 0 ? 3.2 : BAY.minBedroom }));
  if (kitchen === "closed") A.unshift({ kind: "kitchen", w: BAY.kitchen });
  while (sum(B) - sum(A) > 3 && B.length > 1) A.unshift(B.pop()!);
  if (Math.max(sum(A), sum(B)) > F + 1e-6) return undefined;
  const roomy = B.reduce((t, _, i) => t + (i === 0 ? 3.6 : 3.3), 0);
  const wet = F - roomy >= 2.0 && B.length > 0;
  if (wet) B.unshift({ kind: "bath", w: Math.min(3.0, F - roomy) });
  else if (F - roomy >= 1.2 && B.length) B.push({ kind: "storage", w: F - roomy }); // too narrow for the bath: the dressing alone
  // stretch each side to F: the living room and the bedrooms take the extra
  const grow = (xs: { kind: RoomKind; w: number }[], prefer: RoomKind) => {
    const extra = F - sum(xs);
    const who = xs.filter((x) => x.kind === prefer);
    (who.length ? who : xs).forEach((x) => (x.w += extra / (who.length || xs.length)));
  };
  grow(A, "living");
  grow(B, "bedroom");

  // lay out from the door end
  const rooms: BayRoom[] = [];
  const at = (xs: { kind: RoomKind; w: number }[], t0: number, t1: number) => { let s = 0; return xs.map((x) => { const b: Box = [s, s + x.w, t0, t1]; s += x.w; return { kind: x.kind, b }; }); };
  const front = at(A, 0, tb0), back = at(B, tb1, D);
  for (const r of [...front, ...back]) {
    if (r.kind !== "bath") { rooms.push({ kind: r.kind, boxes: [r.b] }); continue; }
    // the wet bay: the bath against the band, the dressing on the facade when there is depth for both
    const [b0, b1, t0, t1] = r.b;
    if (t1 - t0 - 2.4 >= 1.5) { rooms.push({ kind: "bath", boxes: [[b0, b1, t0, t0 + 2.4]] }); rooms.push({ kind: "storage", boxes: [[b0, b1, t0 + 2.4, t1]] }); }
    else rooms.push({ kind: "bath", boxes: [r.b] });
  }

  // the hall runs along the band from the door to 1.1 m past the start of
  // the farthest room that must open off it (bedrooms, and the living room)
  // (a kitchen on the living room's facade can open off the living room; one on the bedrooms' side cannot)
  const needs = [...front.filter((r) => r.kind !== "kitchen"), ...back.filter((r) => r.kind !== "storage")];
  const reach = Math.max(BAY.hall, ...needs.map((r) => Math.min(r.b[1], r.b[0] + 1.1)));
  // then the bath, 1.8–2.6 m, and what is left of the band is a store (or, with a wet bay, the living room's)
  if (!wet && F - reach < 1.8) return undefined;
  const bathW = wet ? 0 : Math.min(type >= 3 ? 2.2 : 2.6, F - reach);
  // an L of hall: the entrance across the band, then a strip along the bedrooms; the rest of
  // the band on the living room's side is wardrobes, a store off the living room
  // (the full-depth part reaches every room on the living room's facade that opens off the hall)
  const col = Math.min(reach, Math.max(BAY.hall + 0.05, ...front.filter((r) => r.kind !== "kitchen").map((r) => Math.min(r.b[1], r.b[0] + 1.1))));
  rooms.push({ kind: "hall", boxes: reach - col > 0.6 ? [[0, col, tb0, tb1], [col, reach, tb1 - BAY.hall, tb1]] : [[0, reach, tb0, tb1]] });
  const living = rooms.find((r) => r.kind === "living")!;
  if (wet) {
    // the band beyond the hall, and beside its strip, opens into the living room; from three rooms
    // the far end of the band is still a second bath when it is wide enough
    if (reach - col > 0.6) living.boxes.push([col, reach, tb0, tb1 - BAY.hall]);
    const rest = F - reach;
    if (type >= 3 && rest >= 1.8) rooms.push({ kind: "bath", boxes: [[F - Math.min(2.4, rest), F, tb0, tb1]] });
    const end = type >= 3 && rest >= 1.8 ? F - Math.min(2.4, rest) : F;
    if (end - reach > 0.3) living.boxes.push([reach, end, tb0, tb1]);
  } else {
  if (reach - col > 0.6) rooms.push({ kind: "storage", boxes: [[col, reach, tb0, tb1 - BAY.hall]] });
  rooms.push({ kind: "bath", boxes: [[reach, reach + bathW, tb0, tb1]] });
  // from three rooms the rest of the band is a second bath, a shower room by the bedrooms; else a store
  const rest = F - reach - bathW;
  if (type >= 3 && rest >= 1.6) rooms.push({ kind: "bath", boxes: [[reach + bathW, F, tb0, tb1]] });
  else {
    if (rest > 0.6) rooms.push({ kind: "storage", boxes: [[reach + bathW, F, tb0, tb1]] });
    else rooms[rooms.length - 1].boxes[0][1] = F;
    // no room in the band for the second bath: an en-suite in the master bedroom's inner corner
    if (type >= 3) {
      const master = rooms.find((r) => r.kind === "bedroom" && r.boxes[0][2] >= tb1 - 1e-6);
      if (master) {
        const [m0, m1, mt0, mt1] = master.boxes[0];
        if (m1 - m0 >= 3.0 && mt1 - mt0 >= 4.2) {
          const e: Box = [m1 - 1.9, m1, mt0, mt0 + 2.2];
          // the room keeps its full width on the facade; the en-suite takes the corner by the band
          master.boxes = [[m0, m1, mt0 + 2.2, mt1], [m0, m1 - 1.9, mt0, mt0 + 2.2]];
          rooms.push({ kind: "bath", boxes: [e] });
        }
      }
    }
  }
  }
  const mid = (tb0 + tb1) / 2;
  // flip so the door is where the landing is
  const flip = opt.doorAtStart === false;
  const m = (b: Box): Box => (flip ? [F - b[1], F - b[0], b[2], b[3]] : b);
  const out = rooms.map((r) => ({ kind: r.kind, boxes: r.boxes.map(m) }));
  const windows = out.map((r, i) => (r.kind === "living" || r.kind === "bedroom" || r.kind === "kitchen" ? i : -1)).filter((i) => i >= 0);
  const liv = front.find((r) => r.kind === "living")!.b;
  const lr: [number, number] = flip ? [F - liv[1], F - liv[0]] : [liv[0], liv[1]];
  return { rooms: out, door: [flip ? F : 0, mid], windows, living: lr, balcony: [lr[0] + 0.3, lr[1] - 0.3] };
}
